import { alpha, Box, keyframes, Stack, Typography } from '@mui/material';
import { CleaningMission, Unit } from '@silbo/core';
import { colors, SprayIcon } from '@silbo/ui';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatElapsed, lastStartedAt } from '../utils/elapsed';

const sweep = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(170%); }
`;

type CurrentCleaningMissionBannerProps = {
  mission: CleaningMission;
  unit?: Unit;
  now: Date;
};

const CurrentCleaningMissionBanner = ({ mission, unit, now }: CurrentCleaningMissionBannerProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const startedAt = lastStartedAt(mission);

  return (
    <Link
      to="/environmental-cleaning/missions/$id"
      params={{ id: mission.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={3}
        sx={{
          paddingY: 3,
          paddingX: '14px',
          bgcolor: 'common.white',
          border: `3px solid ${colors.green[40]}`,
          borderRadius: '14px',
          boxShadow: `0 4px 12px ${alpha(colors.green[80], 0.12)}`,
        }}
      >
        <Box
          sx={{
            flex: 'none',
            width: 44,
            height: 44,
            borderRadius: '22px',
            bgcolor: colors.green[20],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.green[80],
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              width: '60%',
              background: `linear-gradient(90deg, transparent, ${alpha(colors.green[40], 0.4)}, transparent)`,
              animation: `${sweep} 2.4s ease-in-out infinite`,
            },
            '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none', display: 'none' } },
          }}
        >
          <SprayIcon sx={{ fontSize: 22 }} />
        </Box>
        <Stack flex={1} minWidth={0} gap="2px">
          <Stack
            direction="row"
            alignItems="center"
            gap="6px"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: colors.green[80],
            }}
          >
            <Box sx={{ flex: 'none', width: 8, height: 8, borderRadius: '4px', bgcolor: colors.green[60] }} />
            {startedAt
              ? t('missions.current.since', { duration: formatElapsed(startedAt, now) })
              : t('missions.current.label')}
          </Stack>
          <Stack direction="row" alignItems="baseline" gap={2} minWidth={0}>
            <Typography sx={{ flex: 'none', fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {mission.bed.name}
            </Typography>
            {unit && (
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {unit.name}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Link>
  );
};

export default CurrentCleaningMissionBanner;
